import React from "react";
import { cn } from "@/lib/utils";
import ProjectSelector from "@/components/ui/dropdowns/ProjectSelector";
import { Button } from "@/components/ui/button";
import { PanelLeft } from "lucide-react";
import { useFolderStructureVisibility } from "@/contexts/FolderStructureVisibilityContext";
import { useLocation } from "react-router-dom";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TopNavigationBarProps {
  className?: string;
  width?: string;
  sidebarOpen?: boolean;
  hasFolderStructure?: boolean;
}

export const TopNavigationBar: React.FC<TopNavigationBarProps> = ({
  className = "",
  width = "100%",
  sidebarOpen = true,
  hasFolderStructure = false,
}) => {
  const { t } = usePlatformTranslation();
  const location = useLocation();
  const { toggleFolderStructure, activeFolderTypes, isFolderStructureVisible } = useFolderStructureVisibility();
  
  // Handle toggle button click - toggle folder structure visibility
  const handleToggleClick = () => {
    console.log('[TopNavigationBar] Toggle clicked', {
      pathname: location.pathname,
      isFolderStructureVisible,
      activeFolderTypes: Array.from(activeFolderTypes),
      hasFolderStructure
    });
    
    // Toggle folder structure visibility
    // UnifiedSidebar will automatically restore available folder types when toggled back on
    toggleFolderStructure();
  };
  
  // Show centered collapse icon when sidebar is collapsed and no folder structure
  const showCenteredToggle = !sidebarOpen && !hasFolderStructure;
  
  return (
    <div
      className={cn(
        "fixed top-0 start-0 z-50 h-12 bg-background border-b border-border",
        "flex items-center transition-all duration-200 ease-in-out",
        showCenteredToggle ? "px-4 justify-center" : "pe-4",
        className
      )}
      style={{ width }}
    >
      {showCenteredToggle ? (
        /* Centered collapse icon when sidebar collapsed and no folder structure */
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleToggleClick}
          title="Toggle folder structure"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      ) : (
        <>
          {/* Left side - Game Selector (hidden only when sidebar collapsed AND no folder structure) */}
          {(sidebarOpen || hasFolderStructure) && (
            <div className="flex items-center">
              <ProjectSelector />
            </div>
          )}

          {/* Center content */}
          <div className="flex-1 flex items-center justify-center">
            {/* Placeholder for future content like breadcrumbs, search, etc. */}
          </div>

          {/* Right side - Toggle folder structure */}
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleToggleClick}
              title={t('platform.layout.side-bars.toggle_folder_structure_title')}
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
